import React, {useLayoutEffect, useRef} from "react";
import {useFrame} from "@react-three/fiber";
import {Html} from "@react-three/drei";
import {GUIDING_AXIS_COLORS} from "./guidingAxisColors";
import {getCubeOrigin} from "./snapPlacement";

const AXIS_KEYS = ["x", "y", "z"];
const AXIS_VERTEX_PAIR = {x: [0, 1], y: [2, 3], z: [4, 5]};

const hexToRgbUnit = (hex) => {
    const normalized = String(hex || "#000000").replace("#", "");
    const full = normalized.length === 3
        ? normalized.split("").map((c) => c + c).join("")
        : normalized;
    const n = Number.parseInt(full, 16);
    if (!Number.isFinite(n)) return [0, 0, 0];
    return [
        ((n >> 16) & 255) / 255,
        ((n >> 8) & 255) / 255,
        (n & 255) / 255,
    ];
};

const applyAxesHelperColors = (helper, emphasizedAxis) => {
    const colors = helper?.geometry?.attributes?.color;
    if (!colors) return;
    AXIS_KEYS.forEach((axisKey) => {
        const [i0, i1] = AXIS_VERTEX_PAIR[axisKey];
        let rgb;
        if (!emphasizedAxis) {
            rgb = axisKey === "x"
                ? [1, 0, 0]
                : axisKey === "y"
                    ? [0, 1, 0]
                    : [0, 0, 1];
        } else if (axisKey === emphasizedAxis) {
            rgb = hexToRgbUnit(GUIDING_AXIS_COLORS[axisKey]);
        } else {
            const base = hexToRgbUnit(GUIDING_AXIS_COLORS[axisKey]);
            rgb = base.map((v) => v * 0.45);
        }
        colors.setXYZ(i0, rgb[0], rgb[1], rgb[2]);
        colors.setXYZ(i1, rgb[0], rgb[1], rgb[2]);
    });
    colors.needsUpdate = true;
};

const AxisLabel = ({children, color}) => (
    <Html
        center
        zIndexRange={[50, 0]}
        style={{pointerEvents: "none", userSelect: "none"}}
    >
        <div
            style={{
                color,
                fontSize: 14,
                fontWeight: 800,
                fontFamily: "Arial, sans-serif",
                lineHeight: 1,
                textShadow: "0 0 3px #fff, 0 0 3px #fff, 1px 1px 0 #fff, -1px -1px 0 #fff",
                background: "rgba(255,255,255,0.85)",
                borderRadius: 4,
                padding: "2px 5px",
                border: `1px solid ${color}`,
            }}
        >
            {children}
        </div>
    </Html>
);

/**
 * Оси в углу зелёного ориентира (ReferenceCube / EmbeddedReferenceCube).
 * Точка: getCubeOrigin(sizeM) относительно родителя (как CubeAxes на вкладке 3D модуля).
 * worldAlignedInParent — компенсировать поворот родителя, чтобы оси оставались мировыми X/Y/Z.
 */
const ModuleCubeAxes = ({
    sizeM,
    worldAlignedInParent = false,
    /** x | y | z — ось вращения в режиме свободного поворота */
    emphasizedAxis = null,
}) => {
    const groupRef = useRef();
    const axesHelperRef = useRef();
    const {width, height, depth} = sizeM;
    const len = Math.max(width, height, depth) * 1.25;
    const origin = getCubeOrigin(sizeM);

    useLayoutEffect(() => {
        const root = groupRef.current;
        if (!root) return;
        root.position.set(origin.x, origin.y, origin.z);
        if (!worldAlignedInParent) {
            root.quaternion.identity();
            root.scale.set(1, 1, 1);
        }
    }, [origin.x, origin.y, origin.z, worldAlignedInParent]);

    useFrame(() => {
        if (!worldAlignedInParent) return;
        const root = groupRef.current;
        const parent = root?.parent;
        if (!root || !parent) return;
        root.quaternion.copy(parent.quaternion).invert();
        root.scale.set(1, 1, 1);
    });

    useLayoutEffect(() => {
        const helper = axesHelperRef.current;
        if (!helper) return;
        applyAxesHelperColors(helper, emphasizedAxis);
    }, [emphasizedAxis, len]);

    const labelColor = (axisKey) => {
        if (!emphasizedAxis) return GUIDING_AXIS_COLORS[axisKey];
        if (axisKey === emphasizedAxis) return GUIDING_AXIS_COLORS[axisKey];
        return `${GUIDING_AXIS_COLORS[axisKey]}88`;
    };

    return (
        <group ref={groupRef} userData={{module3dRole: "helper"}}>
            <axesHelper ref={axesHelperRef} args={[len]} raycast={() => null}/>
            <group position={[len + 0.06, 0, 0]}>
                <AxisLabel color={labelColor("x")}>X</AxisLabel>
            </group>
            <group position={[0, len + 0.06, 0]}>
                <AxisLabel color={labelColor("y")}>Y</AxisLabel>
            </group>
            <group position={[0, 0, len + 0.06]}>
                <AxisLabel color={labelColor("z")}>Z</AxisLabel>
            </group>
        </group>
    );
};

export default ModuleCubeAxes;
